import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'

import RequestForProposalFeedbackSuccessState from './RequestForProposalFeedbackSuccessState'
import Box from '@mui/material/Box'
import { useSendFeedbackMutation } from 'proposals/feedback/queries/useFeedbackMutations'

const ThumbUp = styled(Box)<{ isPositive: boolean | null }>(({ isPositive }) => ({
  marginTop: '8px',
  transition: 'all 0.5s ease',
  color: isPositive ? '#27AE60' : '#dedede',
  '&:hover': {
    color: '#27AE60'
  }
}))

const ThumbDown = styled(Box)<{ isNegative: boolean | null }>(({ isNegative }) => ({
  marginTop: '8px',
  marginLeft: '8px',
  transition: 'all 0.5s ease',
  color: isNegative ? '#F44336' : '#dedede',
  '&:hover': {
    color: '#F44336'
  }
}))

const SendButton = styled(Button)(({ theme: { spacing } }) => ({
  marginTop: spacing(5),
  color: 'white',
  float: 'right'
}))

const RequestForProposalFeedback = () => {
  const { t } = useTranslation()
  const [isPositive, setIsPositive] = useState<boolean | null>(null)
  const [isNegative, setIsNegative] = useState<boolean | null>(null)
  const { token } = useParams<{ token: string }>()

  const [aboutRequestForProposal, setAboutRequestForProposal] = useState<string>('')
  const [aboutForm, setAboutForm] = useState<string>('')
  const [aboutService, setAboutService] = useState<string>('')

  const { mutate: sendFeedback, isPending: isLoading, isSuccess } = useSendFeedbackMutation(token ?? '')

  const onPositiveClick = () => {
    setIsPositive(true)
    setIsNegative(false)
  }
  const onNegativeClick = () => {
    setIsPositive(false)
    setIsNegative(true)
  }

  const handleSend = () => {
    const payload = {
      isPositiveFeedback: isPositive,
      aboutRequestForProposal: aboutRequestForProposal,
      aboutForm: aboutForm,
      aboutService: aboutService
    }
    sendFeedback(payload)
  }

  const handleAboutRequestForProposalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAboutRequestForProposal(event.target.value)
  }
  const handleAboutFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAboutForm(event.target.value)
  }
  const handleAboutServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAboutService(event.target.value)
  }

  if (isSuccess) {
    return <RequestForProposalFeedbackSuccessState />
  }

  return (
    <div>
      <Typography>{t('was-submitting-a-proposal-easy')}</Typography>
      <IconButton
        disableRipple
        aria-label="thumb up"
        onClick={onPositiveClick}
        size="large"
        sx={{ backgroundColor: 'inherit !important' }}
      >
        <ThumbUp isPositive={isPositive}>
          <ThumbUpIcon sx={{ fontSize: '50px' }} />
        </ThumbUp>
      </IconButton>
      <IconButton
        disableRipple
        aria-label="thumb down"
        onClick={onNegativeClick}
        size="large"
        sx={{ backgroundColor: 'inherit !important' }}
      >
        <ThumbDown isNegative={isNegative}>
          <ThumbDownIcon sx={{ fontSize: '50px' }} />
        </ThumbDown>
      </IconButton>
      <Typography sx={{ marginTop: 3 }}>{t('what-information-was-missing-from-the-request-for-proposal')}</Typography>
      <div>
        <TextField
          fullWidth
          InputProps={{
            sx: { paddingTop: 2 }
          }}
          id="AboutRequestForProposal"
          multiline
          minRows={2}
          variant="filled"
          value={aboutRequestForProposal}
          onChange={handleAboutRequestForProposalChange}
        />
      </div>
      <Typography sx={{ marginTop: 5 }}>{t('how-could-the-proposal-form-be-improved')}</Typography>
      <div>
        <TextField
          fullWidth
          InputProps={{
            sx: { paddingTop: 2 }
          }}
          id="AboutForm"
          multiline
          minRows={2}
          variant="filled"
          value={aboutForm}
          onChange={handleAboutFormChange}
        />
      </div>
      <Typography sx={{ marginTop: 5 }}>{t('general-feedback-of-the-service')}</Typography>
      <div>
        <TextField
          fullWidth
          InputProps={{
            sx: { paddingTop: 2 }
          }}
          id="AboutService"
          multiline
          minRows={2}
          variant="filled"
          value={aboutService}
          onChange={handleAboutServiceChange}
        />
      </div>
      <SendButton variant="contained" color="primary" onClick={handleSend} disabled={isLoading}>
        {t('send-feedback')}
      </SendButton>
    </div>
  )
}

export default RequestForProposalFeedback
